import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ButtonView, ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import sounds from 'src/lib/sounds';
import i18n from 'src/lib/i18n/i18n';

type CloseHandler = () => void;

export default class PopupJankenInfo extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.INFO,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    // Ensure box renders above bg overlay
    this.box.updateOpts({ zIndex: 1 });

    // Tap anywhere to dismiss
    new ButtonView({
      superview: this.box,
      width,
      height,
      zIndex: 1,
      onClick: () => {
        sounds.playSound('ok');
        opts.close();
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/janken/popup',
        'assets/ui/janken/intro/en',
        'assets/ui/janken/intro/ja',
      ]),
    ]);
  }

  public async init() {
    super.init({});
  }

  public async fadeIn() {
    await super.fadeIn();
    await this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);

    const locale = StateObserver.getLocale();
    const en = locale === 'en';
    const boxW = this.box.style.width;

    // Title
    const titleW = downscale(en ? 838 : 762);
    const titleH = downscale(en ? 277 : 282);
    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/janken/popup/title_${locale}.png`,
      width: titleW,
      height: titleH,
      scaleMethod: 'contain',
      x: (boxW - titleW) / 2,
      y: 99,
    });
    await this.showAnimated(title);

    // "How to Play" text
    const howToPlay = new LangBitmapFontTextView({
      superview: this.box,
      width: 342,
      height: 53,
      x: (boxW - 342) / 2,
      y: 290,
      align: 'center',
      verticalAlign: 'center',
      size: 40,
      color: '#FFFFFF',
      font: bitmapFonts('Title'),
      canHandleEvents: false,
      localeText: () => i18n('janken.howToPlay'),
    });
    await this.showAnimated(howToPlay);

    // Step 1: Press START to randomize a Janken sign
    const step1W = downscale(en ? 608 : 606);
    const step1H = downscale(en ? 319 : 312);
    const step1 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/janken/intro/${locale}/instructions.png`,
      width: step1W,
      height: step1H,
      scaleMethod: 'contain',
      x: 218 - step1W / 2,
      y: 478 - step1H / 2,
    });
    await this.showAnimated(step1);

    // Arrow 1 (pointing down-right)
    const arrowW = downscale(119);
    const arrowH = downscale(121);
    const arrow1 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/superLollipop/popupInfo/arrow.png',
      width: arrowW,
      height: arrowH,
      scaleMethod: 'contain',
      x: 314,
      y: 595,
      r: 1.5,
      flipY: true,
    });
    await this.showAnimated(arrow1);

    // Step 2: Select friends to challenge
    const step2W = downscale(552);
    const step2H = downscale(en ? 344 : 337);
    const step2 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/janken/intro/${locale}/friends.png`,
      width: step2W,
      height: step2H,
      scaleMethod: 'contain',
      x: 528 - step2W / 2,
      y: 699 - step2H / 2,
    });
    await this.showAnimated(step2);

    // Arrow 2 (pointing down-left, flipped)
    const arrow2 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/superLollipop/popupInfo/arrow.png',
      width: arrowW,
      height: arrowH,
      scaleMethod: 'contain',
      x: 369,
      y: 816,
      r: 1.7,
    });
    await this.showAnimated(arrow2);

    // Step 3: Winner gets lucky items
    const step3W = downscale(552);
    const step3H = downscale(342);
    const step3 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/janken/intro/${locale}/items.png`,
      width: step3W,
      height: step3H,
      scaleMethod: 'contain',
      x: 230 - step3W / 2,
      y: 872 - step3H / 2,
    });
    await this.showAnimated(step3);

    // Footer: You can also receive Janken challenges from your friends
    const footerW = downscale(794);
    const footerH = downscale(146);
    const footer = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/janken/intro/${locale}/challenges.png`,
      width: footerW,
      height: footerH,
      scaleMethod: 'contain',
      x: (boxW - footerW) / 2,
      y: 1010,
    });
    await this.showAnimated(footer);

    // "Tap to Continue" text
    const tapToContinue = new LangBitmapFontTextView({
      superview: this.box,
      width: 526,
      height: 53,
      x: (boxW - 526) / 2,
      y: 1132,
      align: 'center',
      verticalAlign: 'center',
      size: 40,
      color: '#FFC700',
      font: bitmapFonts('Title'),
      canHandleEvents: false,
      localeText: () => i18n('janken.tapToContinue'),
    });
    await this.showAnimated(tapToContinue);
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0, centerAnchor: true });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.playSound('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
